import { useEffect, useRef } from 'react';

import { ROLES } from '../core/constants/roles';
import { canViewPrivateRunTraces } from '../core/rules/integrityAccess';
import { shouldPersistIntegritySuppression } from '../core/rules/integritySuppression';
import {
  signalKindLabelKey,
  formatIntegritySignal,
  normalizePrivateRunTrace,
} from '../core/models/PrivateRunTrace';
import {
  buildPrivateRunTrace,
  highestSignalSeverity,
  signalFingerprint,
  traceHasDangerSignals,
} from '../services/integrity/privateRunService';
import type { IntegrityInboxMatch } from '../core/notifications/integrityInbox';
import { useDataSync } from './useDataSync';

/** Match cloud scheduler — integrity scans are infrequent. */
const SCAN_INTERVAL_MS = 5 * 60_000;

/** Stable noop so inactive roles do not open Firestore listeners every render. */
const NOOP_ADAPTER = { subscribe: () => () => {} };

interface DataAdapterLike {
  subscribe: (col: string, cb: (data: unknown) => void) => () => void;
  appendPrivateRunTrace?: (trace: unknown) => Promise<unknown>;
}

interface PushFn {
  (fields: Record<string, unknown>, source?: string): unknown;
}

interface UsePrivateRunMonitorParams {
  role: string;
  lang: string;
  t: Record<string, string>;
  push: PushFn;
  dismissMatchingIntegrity?: (match: IntegrityInboxMatch) => void;
  adapter: DataAdapterLike | null;
  enabled?: boolean;
}

function pushIntegrityAlert(
  trace: ReturnType<typeof normalizePrivateRunTrace>,
  lang: string,
  t: Record<string, string>,
  push: PushFn,
) {
  if (trace.signals.length === 0) return;

  const top = trace.signals[0];
  const es = lang === 'es';

  push({
    cat: 'integrity',
    sev: highestSignalSeverity(trace),
    urgent: traceHasDangerSignals(trace),
    unread: true,
    title: es
      ? `Alerta de integridad · ${t[signalKindLabelKey(top.kind)] ?? top.kind}`
      : `Integrity alert · ${t[signalKindLabelKey(top.kind)] ?? top.kind}`,
    body: formatIntegritySignal(top, t),
    meta: {
      runId: trace.runId,
      signalCode: top.code,
      signalCodes: trace.signals.map((s) => s.code),
      ...(top.houseId ? { houseId: top.houseId } : {}),
      target: 'god:integrity',
    },
  }, 'scheduler');
}

function fingerprintFromRawTraces(rawTraces: unknown[]): string {
  if (!rawTraces.length) return '';
  const latest = normalizePrivateRunTrace(rawTraces[0] as Record<string, unknown>);
  return signalFingerprint(latest.signals);
}

/** God-only: infrequent private integrity runs; alert only on new signal patterns. */
export function usePrivateRunMonitor({
  role,
  lang,
  t,
  push,
  dismissMatchingIntegrity,
  adapter,
  enabled = true,
}: UsePrivateRunMonitorParams) {
  const canMonitor = enabled && canViewPrivateRunTraces({ role }) && !!adapter;
  const syncAdapter = canMonitor ? adapter! : NOOP_ADAPTER;

  const events = useDataSync<Record<string, unknown>[]>(
    syncAdapter as Parameters<typeof useDataSync<Record<string, unknown>[]>>[0],
    'auditLog',
    [],
  );
  const houseRows = useDataSync<Record<string, unknown>[]>(
    syncAdapter as Parameters<typeof useDataSync<Record<string, unknown>[]>>[0],
    'houses',
    [],
  );
  const rawTraces = useDataSync<Record<string, unknown>[]>(
    syncAdapter as Parameters<typeof useDataSync<Record<string, unknown>[]>>[0],
    'privateRunTraces',
    [],
  );

  const lastAlertFingerprintRef = useRef<string>(fingerprintFromRawTraces(rawTraces));
  const lastStoredFingerprintRef = useRef<string>(fingerprintFromRawTraces(rawTraces));
  const eventsRef = useRef(events);
  const houseRowsRef = useRef(houseRows);

  useEffect(() => {
    eventsRef.current = events;
    houseRowsRef.current = houseRows;
  }, [events, houseRows]);

  // Clear inbox rows for signals already closed as suppressed / false-positive.
  useEffect(() => {
    if (!canMonitor || !dismissMatchingIntegrity || !rawTraces.length) return;
    for (const raw of rawTraces) {
      const trace = normalizePrivateRunTrace(raw as Record<string, unknown>);
      for (const signal of trace.signals) {
        if (!shouldPersistIntegritySuppression(String(signal.disposition ?? 'open'))) continue;
        dismissMatchingIntegrity({
          runId: trace.runId,
          signalCode: signal.code,
          houseId: signal.houseId,
        });
      }
    }
  }, [canMonitor, dismissMatchingIntegrity, rawTraces]);

  useEffect(() => {
    if (!canMonitor || !adapter?.appendPrivateRunTrace) return undefined;

    let cancelled = false;

    const runScan = async () => {
      const auditEvents = eventsRef.current;
      const houses = houseRowsRef.current;
      if (!auditEvents.length && !houses.length) return;

      const trace = buildPrivateRunTrace({
        auditEvents: auditEvents as Parameters<typeof buildPrivateRunTrace>[0]['auditEvents'],
        houses: houseRows as unknown as Parameters<typeof buildPrivateRunTrace>[0]['houses'],
        source: 'scheduled',
      });

      if (trace.signals.length === 0) return;

      const fingerprint = signalFingerprint(trace.signals);
      if (fingerprint === lastStoredFingerprintRef.current) return;

      await adapter.appendPrivateRunTrace!(trace);
      lastStoredFingerprintRef.current = fingerprint;

      if (cancelled) return;
      if (fingerprint === lastAlertFingerprintRef.current) return;
      lastAlertFingerprintRef.current = fingerprint;

      pushIntegrityAlert(trace, lang, t, push);
    };

    const id = setInterval(runScan, SCAN_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [adapter, canMonitor, lang, push, t]);

  useEffect(() => {
    if (!canMonitor || adapter?.appendPrivateRunTrace) return undefined;
    if (!rawTraces.length) return undefined;

    const latest = normalizePrivateRunTrace(rawTraces[0] as Record<string, unknown>);
    if (latest.signals.length === 0) return undefined;

    // Do not re-alert patterns that were already closed on this trace.
    const openSignals = latest.signals.filter(
      (s) => !shouldPersistIntegritySuppression(String(s.disposition ?? 'open')),
    );
    if (openSignals.length === 0) return undefined;

    const fingerprint = signalFingerprint(latest.signals);
    if (fingerprint === lastAlertFingerprintRef.current) return undefined;
    lastAlertFingerprintRef.current = fingerprint;

    pushIntegrityAlert({ ...latest, signals: openSignals }, lang, t, push);
    return undefined;
  }, [adapter, canMonitor, rawTraces, lang, push, t]);

  return { canMonitor, role: canMonitor ? ROLES.GOD : role };
}
